import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Box, Chip, Drawer, Stack, Tooltip } from "@mui/material";
import SecondaryButton from "../buttons/secondary-button";
import Heading5 from "../typography/heading-05";
import Body2 from "../typography/body-02";
import Body3 from "../typography/body-03";
import ProcessingModal from "../modal/processing-modal";
import SuccessModal from "../modal/success-modal";
import DocumentsTrackerDrawer from "../temporary-drawer/components/documents-tracker";
import { setEnhancedConditionStatus } from "../../features/enhanced-condition-status/enhancedConditionStatusSlice";
import { convertUTCToEastern } from "../../utils/timezone-offset";
import { VERBIAGE_CONSTANTS } from "../../utils/verbiage.constants";
import styles from "./index.module.scss";

const docsCountOf = (row, extraDocs) => (Number(row?.docsCount) || 0) + (extraDocs[row?.id] || 0);

const ConditionRow = ({ row, docsCount, expanded, onUpload, onToggleView, disabled }) => {
	const met = docsCount > 0;
	const description = row.description || "";
	return (
		<Box className={`${styles.row} ${met ? styles.rowMet : ""}`} data-testid={`aura-condition-${row.id}`}>
			<Box className={styles.rowGrid}>
				<Tooltip title={description} placement="top-start" disableHoverListener={!description}>
					<Stack direction="column" className={styles.nameCell}>
						<Body2 text={row.title} fontType="semibold" />
						{description && <Body3 text={description} extraClass={styles.preview} />}
					</Stack>
				</Tooltip>
				<Box>
					{/* Spec (AURA_Flow_Conditions_to_Display): the condition's own Encompass status.
					    The pill colour still shows whether documents are attached. */}
					<Chip
						label={row.status || (met ? "Docs uploaded" : "Open")}
						size="small"
						className={met ? styles.pillDone : styles.pillOpen}
					/>
				</Box>
				<Box>{row.category && <Chip label={row.category} size="small" className={styles.pillCategory} />}</Box>
				<Box>{row.priorTo && <Chip label={row.priorTo} size="small" className={styles.pillPriorTo} />}</Box>
				<Body3 text={convertUTCToEastern(row.createdDate) || "—"} extraClass={styles.added} />
				<Body3 text={String(docsCount)} extraClass={styles.docs} />
				<Box className={styles.action}>
					<SecondaryButton
						text={met ? "View" : "Upload"}
						onClick={() => (met ? onToggleView(row.id) : onUpload(row))}
						disabled={disabled && !met}
					/>
				</Box>
			</Box>
			{expanded && (
				<Stack direction="column" gap={1} className={styles.detail}>
					{description && <Body3 text={description} />}
					<Body3 text={`${docsCount} document${docsCount === 1 ? "" : "s"} attached`} extraClass={styles.preview} />
					<Box>
						<SecondaryButton text="Upload another document" onClick={() => onUpload(row)} disabled={disabled} />
					</Box>
				</Stack>
			)}
		</Box>
	);
};

const ConditionGroup = ({ title, subtitle, rows, extraDocs, expandedId, onUpload, onToggleView, disabled }) => {
	if (!rows.length) return null;
	return (
		<Stack direction="column" gap={1} mb={2}>
			<Stack direction="row" alignItems="baseline" gap={1} flexWrap="wrap">
				<Heading5 text={title} />
				<Body3 text={`— ${subtitle}`} extraClass={styles.subtitle} />
			</Stack>
			<Box className={`${styles.rowGrid} ${styles.headerRow}`}>
				{["Condition", "Status", "Category", "Prior to", "Added", "Docs", ""].map((label) => (
					<Body3 key={label || "action"} text={label} extraClass={styles.headerCell} />
				))}
			</Box>
			{rows.map((row) => (
				<ConditionRow
					key={row.id}
					row={row}
					docsCount={docsCountOf(row, extraDocs)}
					expanded={expandedId === row.id}
					onUpload={onUpload}
					onToggleView={onToggleView}
					disabled={disabled}
				/>
			))}
		</Stack>
	);
};

// LC-696 / PathA-5.05, 5.07, 5.15–5.17, 6.01–6.02, PathC-4.01–4.02: documentation
// conditions in two labelled lists with the Docs & Condition Tracker's columns,
// one-click Upload per row, a green "Docs uploaded" state and a live counter.
// Uploads go through the tracker's own drawer (eFolder, chunking, S3 fallback).
// `disabled` (AURA running) turns off every Upload; viewing stays available.
const AuraConditionList = ({
	conditions,
	loanGuid,
	showPreliminary = true,
	onRefresh,
	onError,
	disabled = false,
}) => {
	const dispatch = useDispatch();
	// Docs added in this session, shown at once while the refetch catches up.
	const [extraDocs, setExtraDocs] = useState({});
	const [uploadRow, setUploadRow] = useState(null);
	const [expandedId, setExpandedId] = useState(null);
	const [processing, setProcessing] = useState(false);
	const [processingText, setProcessingText] = useState("");
	const [successText, setSuccessText] = useState("");
	const [successOpen, setSuccessOpen] = useState(false);

	const conditionModel = conditions?.conditionModel;
	useEffect(() => {
		// The upload drawer picks enhanced vs standard from this flag.
		if (conditionModel) {
			dispatch(setEnhancedConditionStatus(conditionModel === "enhanced" ? "Y" : "N"));
		}
	}, [conditionModel, dispatch]);

	// Fresh server counts replace the optimistic ones.
	useEffect(() => {
		setExtraDocs({});
	}, [conditions?.signature]);

	const minimum = conditions?.minimumSubmission || [];
	const preliminary = showPreliminary ? conditions?.preliminaryCredit || [] : [];
	// Live counter spans every list shown (mockup: "0 of 5 conditions with docs uploaded").
	const shown = [...minimum, ...preliminary];
	const uploaded = shown.filter((row) => docsCountOf(row, extraDocs) > 0).length;

	if (!shown.length) {
		return null;
	}

	const handleUploadSuccess = async ({ fileCount = 1 } = {}) => {
		const uploadedId = uploadRow?.id;
		if (uploadedId) {
			setExtraDocs((prev) => ({ ...prev, [uploadedId]: (prev[uploadedId] || 0) + (fileCount || 1) }));
		}
		setUploadRow(null);
		await onRefresh?.();
	};

	const uploadOption = uploadRow && {
		id: uploadRow.id,
		title: uploadRow.title,
		label: uploadRow.title,
		name: uploadRow.title,
		entityName: uploadRow.title,
		conditionType: uploadRow.conditionType,
		category: uploadRow.category,
		description: uploadRow.description,
	};

	return (
		<Box className={styles.root}>
			<ProcessingModal open={processing} text={processingText} />
			<SuccessModal open={successOpen} text={successText} handleClose={() => setSuccessOpen(false)} />

			<Body3
				text={VERBIAGE_CONSTANTS.AURA_DOCS_UPLOADED_COUNTER(uploaded, shown.length)}
				extraClass={styles.counter}
			/>

			<ConditionGroup
				title={VERBIAGE_CONSTANTS.MIN_SUBMISSION_TITLE}
				subtitle={VERBIAGE_CONSTANTS.MIN_SUBMISSION_SUBTITLE}
				rows={minimum}
				extraDocs={extraDocs}
				expandedId={expandedId}
				onUpload={setUploadRow}
				disabled={disabled}
				onToggleView={(id) => setExpandedId((prev) => (prev === id ? null : id))}
			/>
			<ConditionGroup
				title={VERBIAGE_CONSTANTS.PRELIM_CREDIT_TITLE}
				subtitle={VERBIAGE_CONSTANTS.PRELIM_CREDIT_SUBTITLE}
				rows={preliminary}
				extraDocs={extraDocs}
				expandedId={expandedId}
				onUpload={setUploadRow}
				disabled={disabled}
				onToggleView={(id) => setExpandedId((prev) => (prev === id ? null : id))}
			/>

			<Drawer
				anchor="right"
				open={!!uploadRow}
				onClose={() => setUploadRow(null)}
				PaperProps={{ className: styles.drawerPaper }}
			>
				{uploadOption && (
					<DocumentsTrackerDrawer
						handleCloseDrawer={() => setUploadRow(null)}
						handleOpenProcessingModal={() => setProcessing(true)}
						handleCloseProcessingModal={() => setProcessing(false)}
						handleOpenSuccessModal={() => setSuccessOpen(true)}
						setProcessingText={setProcessingText}
						setSuccessText={setSuccessText}
						handleOpenCommonErrorModal={(key) => onError?.(key)}
						drawerData={{
							loanGuid,
							nextConditionsOptions: [uploadOption],
							selectedConditionTypes: [uploadOption.id],
							uploadHeaderText: `Uploading for condition ${uploadOption.label}`,
							uploadSubHeaderText: uploadOption.label,
							hideConditionSelection: true,
							uploadConditionId: uploadOption.id,
							onUploadSuccess: handleUploadSuccess,
						}}
					/>
				)}
			</Drawer>
		</Box>
	);
};

export default AuraConditionList;
